import { getPublicDomainList } from './constants/freeEmailProviders';
import {
  SEARCH_CATEGORIES,
  PROFILER_SEARCH_CATEGORIES,
  PROFILER_INDUSTRY_CODES_SEARCH_CATEGORIES
} from './constants/searchCategories';
import {
  SEARCH_FIELDS,
  FIELDS_TYPES,
  PROFILER_SEARCH_FIELDS,
  PROFILER_INDUSTRY_CODES_SEARCH_FIELDS
} from './constants/fields';
import { PUNCTUATION, CURRENCIES, NUMBERS } from './constants/notation';
import { matchRoutes } from 'react-router';
import punycode from 'punycode/';

export const matchPath = (paths = [], location) => {
  const matches = matchRoutes(
    paths.map((path) => ({ path })),
    location
  );

  return !!matches;
};

export const mapFieldsToState = (fields = {}) => {
  return Object.fromEntries(
    Object.values(fields).map((fieldConfig) => [
      fieldConfig.NAME,
      fieldConfig.TYPE === FIELDS_TYPES.TYPE_CHECKBOX
        ? false
        : [
              FIELDS_TYPES.TYPE_MULTI_SELECT,
              FIELDS_TYPES.TYPE_SELECT,
              FIELDS_TYPES.TYPE_RADIO
            ].includes(fieldConfig.TYPE)
          ? []
          : ''
    ])
  );
};

export const cleanWebsiteUrl = (url = '') => {
  if (!url) {
    return url;
  }

  return url.replace(/^(https?:\/\/)?(www\.)?/i, '');
};

export const cleanWebsiteUrl_SAFE = (url = '') => {
  if (!url) return '';

  // Remove protocol and www, then lowercase
  let cleaned = url.replace(/^(https?:\/\/)?(www\.)?/i, '').toLowerCase();

  // Transliterate German umlauts and ß (lowercase only)
  cleaned = cleaned
    .replace(/ä/g, 'ae')
    .replace(/ö/g, 'oe')
    .replace(/ü/g, 'ue')
    .replace(/ß/g, 'ss')

    // Transliterate Scandinavian letters (lowercase only)
    .replace(/å/g, 'aa')
    .replace(/æ/g, 'ae')
    .replace(/ø/g, 'oe');

  // If non-ASCII chars remain, convert to punycode
  if (/[^a-z0-9.-]/.test(cleaned)) {
    try {
      cleaned = punycode.toASCII(cleaned);
    } catch {
      // fallback: return cleaned as is
    }
  }

  return cleaned;
};

export const cleanWebsiteUrl_SAFE_LOGO = (cleanedUrl = '') => {
  const foundSlash = typeof cleanedUrl === 'string' ? cleanedUrl.indexOf('/') : -1;
  if (foundSlash !== -1) {
    return cleanedUrl.substring(0, foundSlash + 1);
  }

  return cleanedUrl || '';
};

// TD-1877: Extract first URL from potentially multi-value website strings (e.g. "url1, url2").
// Frontend fix chosen over backend because search results cached in DynamoDB would remain broken.
// Backend cleanup tracked in TD-1878.
export const extractFirstUrl = (url = '') => {
  if (!url) return url;
  return url.split(/[,;]/)[0].trim();
};

export const generateMessage = (
  message = '',
  isValid = false,
  field = null,
  isOnSubmit = false
) => {
  return { msg: message, isValid, field, isOnSubmit };
};

export const isUsingCompanyEmail = (email = '') => {
  if (!email) {
    return false;
  }

  if (typeof email !== 'string') {
    return false;
  }

  if (!email.includes('@')) {
    return false;
  }

  const parts = email.split('@');
  const domain = parts[parts.length - 1];
  if (getPublicDomainList().includes(domain)) {
    return false;
  }

  return true;
};

export function validatePasswordComplexity(password = '') {
  const messages = [
    [password.length >= 8, 'Password must contain at least 8 characters'],
    [/\d/.test(password), 'Password must contain at least 1 number'],
    [/[^a-zA-Z0-9\s]/.test(password), 'Password must contain at least 1 special character'],
    [/[A-Z]/.test(password), 'Password must contain at least 1 uppercase letter'],
    [/[a-z]/.test(password), 'Password must contain at least 1 lowercase letter']
  ].map(([isValid, msg]) => generateMessage(msg, isValid));

  return messages;
}

export const formatNumber = (number = 0, spacer = PUNCTUATION.COMMA, skipEndFormatting = false) => {
  try {
    const testNum = +number;
    if (isNaN(testNum) || !['string', 'number'].includes(typeof number) || number === '') {
      return PUNCTUATION.EMPTY_VALUE;
    }
  } catch (error) {
    return PUNCTUATION.EMPTY_VALUE;
  }

  const formattedNumber = number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, spacer);

  if (!skipEndFormatting) {
    if (formattedNumber.endsWith('.00')) {
      return formattedNumber.slice(0, formattedNumber.length - 3);
    } else if (
      formattedNumber.endsWith('0') &&
      formattedNumber.charAt(formattedNumber.length - 3) === '.'
    ) {
      return formattedNumber.slice(0, formattedNumber.length - 1);
    }
  }

  return formattedNumber;
};

export const formatPercent = (
  number = 0,
  spacer = PUNCTUATION.COMMA,
  skipEndFormatting = false
) => {
  const formattedNumber = formatNumber(number, spacer, skipEndFormatting);
  if (formattedNumber === PUNCTUATION.EMPTY_VALUE) {
    return PUNCTUATION.EMPTY_VALUE;
  }

  return `${formattedNumber}${PUNCTUATION.PERCENT}`;
};

export const formatCurrency = (
  number = 0,
  currencyNotation = CURRENCIES.EUR,
  numberNotation = NUMBERS.MILLION
) => {
  const formattedNumber = formatNumber(number);
  if (formattedNumber === PUNCTUATION.EMPTY_VALUE) {
    return PUNCTUATION.EMPTY_VALUE;
  }

  return `${currencyNotation}${formattedNumber}${numberNotation}`;
};

export const formatCurrencyRange = (
  number1 = 0,
  number2 = 0,
  rangeNotation = PUNCTUATION.EN_DASH,
  currencyNotation = CURRENCIES.EUR,
  numberNotation = NUMBERS.MILLION
) => {
  let first = formatCurrency(number1, currencyNotation, numberNotation);
  let second = formatCurrency(number2, currencyNotation, numberNotation);

  // if ([first, second].includes(PUNCTUATION.EMPTY_VALUE)) {
  //   return PUNCTUATION.EMPTY_VALUE;
  // }

  if (first === PUNCTUATION.EMPTY_VALUE && second === PUNCTUATION.EMPTY_VALUE) {
    return PUNCTUATION.EMPTY_VALUE;
  }

  // if (first === PUNCTUATION.EMPTY_VALUE) {
  //   first = PUNCTUATION.EMPTY_VALUE;
  // }

  // if (second === PUNCTUATION.EMPTY_VALUE) {
  //   second = PUNCTUATION.EMPTY_VALUE;
  // }

  return `${first}${rangeNotation}${second}`;
};

export const formatNumberRange = (
  number1 = 0,
  number2 = 0,
  rangeNotation = PUNCTUATION.EN_DASH,
  showSingleValueLabel = false
) => {
  let first = formatNumber(number1);
  let second = formatNumber(number2);

  // if ([first, second].includes(PUNCTUATION.EMPTY_VALUE)) {
  //   return PUNCTUATION.EMPTY_VALUE;
  // }

  if (first === PUNCTUATION.EMPTY_VALUE && second === PUNCTUATION.EMPTY_VALUE) {
    return PUNCTUATION.EMPTY_VALUE;
  }

  // if (first === PUNCTUATION.EMPTY_VALUE) {
  //   first = PUNCTUATION.EMPTY_VALUE;
  // }

  // if (second === PUNCTUATION.EMPTY_VALUE) {
  //   second = PUNCTUATION.EMPTY_VALUE;
  // }

  if (showSingleValueLabel) {
    if (first === PUNCTUATION.EMPTY_VALUE) {
      return `Max. ${second}`;
    } else if (second === PUNCTUATION.EMPTY_VALUE) {
      return `Min. ${first}`;
    }
  }

  return `${first}${rangeNotation}${second}`;
};

export const getFilledSearchCategoriesCount = (
  stateFields = {},
  categories = SEARCH_CATEGORIES,
  searchFields = SEARCH_FIELDS
) => {
  return Object.fromEntries(
    Object.values(categories).map((section) => {
      // Set for tracking total fields count
      // Range fields with Min and Max suffixes e.g. cagrMin/cagrMax are considered one field in the UI
      const sectionFieldNames = new Set();
      // Array for tracking range fields with populated valid values.
      // It is enough only the Min or Max value to be populated for a range field.
      // Hence, the pair value has to be ignored when counting filled fields.
      const rangeFields = [];
      // Array holding all relevant fields for the given `section`
      const sectionFields = Object.entries(stateFields).filter(
        ([fieldName]) =>
          Object.values(searchFields).find((f) => f.NAME === fieldName)?.SECTION === section
      );
      // Populating the names set and removing Min/Max suffixes in order to count the range fields as one
      sectionFields.forEach(([sfName]) => {
        if (sfName.endsWith('Min') || sfName.endsWith('Max')) {
          sectionFieldNames.add(sfName.slice(0, -3));
        } else {
          sectionFieldNames.add(sfName);
        }
      });
      // Filtering fields which have valid populated values in order to get the `filled` count
      const filled = sectionFields.filter(([name, value]) => {
        // If a range field with a valid value is processed
        if ((name.endsWith('Min') || name.endsWith('Max')) && value.length) {
          // Remove the suffix and check if the pair has already been processed e.g. the name is stored in `rangeFields`
          const nameSliced = name.slice(0, -3);
          if (!rangeFields.find((f) => f === nameSliced)) {
            // If not, store it.
            rangeFields.push(nameSliced);
          } else {
            // If the pair is already processed ignore the current field
            return false;
          }
        }

        return Array.isArray(value)
          ? !!value.length
          : typeof value === 'boolean'
            ? value
            : !!value.length;
      }).length;
      const total = sectionFieldNames.size;
      return [section, { filled, total }];
    })
  );
};

export const getProfilerFilledSearchCategories = (stateFields = {}) => {
  return getFilledSearchCategoriesCount(
    stateFields,
    PROFILER_SEARCH_CATEGORIES,
    PROFILER_SEARCH_FIELDS
  );
};

export const getProfilerIndustriesFilledSearchCategories = (stateFields = {}) => {
  return getFilledSearchCategoriesCount(
    stateFields,
    PROFILER_INDUSTRY_CODES_SEARCH_CATEGORIES,
    PROFILER_INDUSTRY_CODES_SEARCH_FIELDS
  );
};

export const formatDateDDMMYYYY = (date = new Date()) => {
  let d = date;
  if (!(date instanceof Date)) {
    try {
      d = new Date(date);
    } catch (error) {
      console.error(error);
      return '??.??.????';
    }
  }

  let [day, month, year] = [d.getDate(), d.getMonth() + 1, d.getFullYear()];

  if (day < 10) {
    day = '0' + day;
  }

  if (month < 10) {
    month = '0' + month;
  }

  return `${day}.${month}.${year}`;
};

export const getTooltipId = (() => {
  let id = 0;
  return () => {
    id++;
    return 'unique-tooltip' + id;
  };
})();

export const canScrollElement = (node) => {
  if (node == null) {
    return false;
  }

  if (node.scrollHeight > node.clientHeight) {
    return true;
  }

  return false;
};

export const isPhoneOrTablet = (() => {
  const isTouchDevice = () => 'ontouchstart' in window || navigator.maxTouchPoints > 0;
  const isMobileOrTablet = () => {
    const userAgent = navigator.userAgent || navigator.vendor || window.opera;
    return /android|iphone|ipad|ipod/i.test(userAgent);
  };

  const touchDevice = isTouchDevice();
  const mobileOrTablet = isMobileOrTablet();
  return touchDevice && mobileOrTablet;
})();

let pSBCr;
const pSBC = (p, c0, c1, l) => {
  let r,
    g,
    b,
    P,
    f,
    t,
    h,
    i = parseInt,
    m = Math.round,
    a = typeof c1 == 'string';
  if (
    typeof p != 'number' ||
    p < -1 ||
    p > 1 ||
    typeof c0 != 'string' ||
    (c0[0] != 'r' && c0[0] != '#') ||
    (c1 && !a)
  )
    return null;
  if (!pSBCr)
    pSBCr = (d) => {
      let n = d.length,
        x = {};
      if (n > 9) {
        (([r, g, b, a] = d = d.split(',')), (n = d.length));
        if (n < 3 || n > 4) return null;
        ((x.r = i(r[3] == 'a' ? r.slice(5) : r.slice(4))),
          (x.g = i(g)),
          (x.b = i(b)),
          (x.a = a ? parseFloat(a) : -1));
      } else {
        if (n == 8 || n == 6 || n < 4) return null;
        if (n < 6) d = '#' + d[1] + d[1] + d[2] + d[2] + d[3] + d[3] + (n > 4 ? d[4] + d[4] : '');
        d = i(d.slice(1), 16);
        if (n == 9 || n == 5)
          ((x.r = (d >> 24) & 255),
            (x.g = (d >> 16) & 255),
            (x.b = (d >> 8) & 255),
            (x.a = m((d & 255) / 0.255) / 1000));
        else ((x.r = d >> 16), (x.g = (d >> 8) & 255), (x.b = d & 255), (x.a = -1));
      }
      return x;
    };
  ((h = c0.length > 9),
    (h = a ? (c1.length > 9 ? true : c1 == 'c' ? !h : false) : h),
    (f = pSBCr(c0)),
    (P = p < 0),
    (t =
      c1 && c1 != 'c'
        ? pSBCr(c1)
        : P
          ? { r: 0, g: 0, b: 0, a: -1 }
          : { r: 255, g: 255, b: 255, a: -1 }),
    (p = P ? p * -1 : p),
    (P = 1 - p));
  if (!f || !t) return null;
  if (l) ((r = m(P * f.r + p * t.r)), (g = m(P * f.g + p * t.g)), (b = m(P * f.b + p * t.b)));
  else
    ((r = m((P * f.r ** 2 + p * t.r ** 2) ** 0.5)),
      (g = m((P * f.g ** 2 + p * t.g ** 2) ** 0.5)),
      (b = m((P * f.b ** 2 + p * t.b ** 2) ** 0.5)));
  ((a = f.a),
    (t = t.a),
    (f = a >= 0 || t >= 0),
    (a = f ? (a < 0 ? t : t < 0 ? a : a * P + t * p) : 0));
  if (h)
    return (
      'rgb' + (f ? 'a(' : '(') + r + ',' + g + ',' + b + (f ? ',' + m(a * 1000) / 1000 : '') + ')'
    );
  else
    return (
      '#' +
      (4294967296 + r * 16777216 + g * 65536 + b * 256 + (f ? m(a * 255) : 0))
        .toString(16)
        .slice(1, f ? undefined : -2)
    );
};

export const stringToColour = (str = '') => {
  let hash = 0;
  str.split('').forEach((char) => {
    hash = char.charCodeAt(0) + ((hash << 5) - hash);
  });
  let colour = '#';
  for (let i = 0; i < 3; i++) {
    const value = (hash >> (i * 8)) & 0xff;
    colour += value.toString(16).padStart(2, '0');
  }
  return pSBC(0.42, colour, false, true);
};

export const checkBrowser = () => {
  // Get the user-agent string
  let userAgentString = navigator.userAgent;

  // Detect Chrome
  let chromeAgent = userAgentString.indexOf('Chrome') > -1;

  // Detect Internet Explorer
  let IExplorerAgent = userAgentString.indexOf('MSIE') > -1 || userAgentString.indexOf('rv:') > -1;

  // Detect Firefox
  let firefoxAgent = userAgentString.indexOf('Firefox') > -1;

  // Detect Safari
  let safariAgent = userAgentString.indexOf('Safari') > -1;

  // Discard Safari since it also matches Chrome
  if (chromeAgent && safariAgent) safariAgent = false;

  // Detect Opera
  let operaAgent = userAgentString.indexOf('OP') > -1;

  // Discard Chrome since it also matches Opera
  if (chromeAgent && operaAgent) chromeAgent = false;

  return { safariAgent, chromeAgent, IExplorerAgent, operaAgent, firefoxAgent };
};

export const isElementVisibleOnScreen = (el) => {
  const rect = el.getBoundingClientRect();
  return (
    rect.top >= 0 &&
    rect.left >= 0 &&
    rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
    rect.right <= (window.innerWidth || document.documentElement.clientWidth)
  );
};

export const isElementVisibleOnScreenPartially = (el, heightPart = 1, widthPart = 1) => {
  const rect = el.getBoundingClientRect();
  const partialHeight =
    heightPart > 1 ? (el.clientHeight / heightPart) * (heightPart - 1) : el.clientHeight;
  const partialWidth =
    widthPart > 1 ? (el.clientWidth / widthPart) * (widthPart - 1) : el.clientWidth;
  return (
    rect.top >= 0 &&
    rect.left >= 0 &&
    rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) + partialHeight &&
    rect.right <= (window.innerWidth || document.documentElement.clientWidth) + partialWidth
  );
};

export const clearDistanceWhenCityCleared = (
  fieldsState = {},
  field = {},
  value,
  cityZipFieldConst,
  distanceFieldConst
) => {
  if (field?.NAME === cityZipFieldConst?.NAME && !`${value || ''}`.trim()) {
    fieldsState[distanceFieldConst?.NAME] = '';
  }
};

/**
 * Calculate age from birthday string (format: YYYY-MM-DD or similar) or birth year
 * Handles variable granularity: full date (YYYY-MM-DD), year+month, or year-only
 * @param {string} birthday - Full birth date (e.g., "1990-05-15") - optional
 * @param {number} birthYear - Birth year only (e.g., 1990) - optional fallback
 * @returns {number|null} Age in years or null if not calculable
 */
export const calculateAge = (birthday, birthYear) => {
  const today = new Date();
  const currentYear = today.getFullYear();

  // Try full birthday first for accurate age
  if (birthday) {
    const birthDate = new Date(birthday);
    if (!isNaN(birthDate.getTime())) {
      let age = currentYear - birthDate.getFullYear();
      const monthDiff = today.getMonth() - birthDate.getMonth();

      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
        age--;
      }

      return age > 0 ? age : null;
    }
  }

  // Fall back to birth year (less accurate but often available)
  if (birthYear && !isNaN(birthYear)) {
    const age = currentYear - birthYear;
    return age > 0 ? age : null;
  }

  return null;
};

/**
 * Get profile icon path based on gender
 * Used for individual/person profile displays
 * @param {string} gender - Gender value ('M', 'F', 'Male', 'Female', or other)
 * @returns {string} Icon path
 */
export const getProfileIcon = (gender) => {
  const ICONS = {
    PERSON: '/icons/clean/person.svg',
    MALE: '/icons/clean/male.svg',
    FEMALE: '/icons/clean/female.svg'
  };

  if (!gender) return ICONS.PERSON;

  const normalizedGender = gender.toLowerCase();
  if (normalizedGender === 'm' || normalizedGender === 'male') return ICONS.MALE;
  if (normalizedGender === 'f' || normalizedGender === 'female') return ICONS.FEMALE;

  return ICONS.PERSON;
};
